import React, {useEffect} from "react"
import NavbarUserDropdown from "./NavbarUserDropdown"
import NavbarDeveloperDropdown from "./NavbarDeveloperDropdown"
import NavbarTimezoneDropdown from "./NavbarTimezoneDropdown"
import {useIsOnBehalfUser, useIsManagedSolutionsBankingUser, useUserClaimsData} from "../../services/auth"
import {useLocalStorage, useLocation} from "react-use"
import classNames from "classnames"
import Icon from "../Icon/Icon"
import {getCurrentEnvironmentName} from "../../utilities/environment"
import {Link, NavLink} from "react-router-dom"
import NavbarOnBelalfBanner from "./NavbarOnBelalfBanner"
import LogoLight from "../../images/logo-light.svg"
import {IcomoonIconName} from "../Icon/icons"
import LogoU from "../../images/logo-u.svg"
import {useOrgBankingSetup} from "../../services/orgBankingSetup"

export function NavbarEnvironmentIndicator({isExpanded}: {isExpanded: boolean}) {
	let envTitle
	let icon: IcomoonIconName
	const env = getCurrentEnvironmentName()
	const {setup} = useOrgBankingSetup()
	const isManagedSolutionsBankingUser = useIsManagedSolutionsBankingUser()

	switch (env) {
		case "pilot": {
			envTitle = "Pilot"
			icon = "flash-enable-allow-1--interface-essential"
			break
		}
		case "live": {
			envTitle = "Live"
			icon = "astronomy-rocket-1--school-science"
			break
		}
		case "sandbox": {
			envTitle = "Sandbox"
			icon = "shape-3d-box"
			break
		}
	}

	const hideBuildPath = !setup || setup === "NotSet"
	const buildPath = setup === "ReadyToLaunchBanking" || isManagedSolutionsBankingUser ? "Managed Solutions" : "Custom"

	return (
		<div className={classNames("navbar-environment-indicator-wrapper", isExpanded && "is-expanded", env)}>
			<div className={classNames("navbar-environment-indicator", !hideBuildPath && "with-build-path")}>
				{isExpanded ? (
					<>
						<div className="environment-indicator">
							<Icon size={12} icon={icon} />
							{envTitle}
						</div>
						{!hideBuildPath && <div className="build-path-indicator">{buildPath}</div>}
					</>
				) : (
					<img src={LogoU} alt="Unit" />
				)}
			</div>
		</div>
	)
}

function SandboxBanner({isOpen}: {isOpen: boolean}) {
	return (
		<a
			href="https://www.unit.co/book-demo"
			target="_blank"
			rel="noreferrer"
			data-tracking-label="book-1-on-1"
			className={classNames(isOpen && "open")}
		>
			{"Want some help getting started? "}
			Book 1:1 time with Unit →
		</a>
	)
}

function PilotBanner({isOpen}: {isOpen: boolean}) {
	return (
		<Link to="/pilot-overview" className={classNames(isOpen && "open")}>
			{"You're in Pilot, where you can test our core features using real funds and accounts."} Learn more →
		</Link>
	)
}

function AnnouncementBanner() {
	return (
		<a
			href="https://www.unit.co/a-note-from-our-ceo"
			target="_blank"
			rel="noreferrer"
			data-tracking-label="message-from-unit-on-svb"
		>
			<Icon icon={"email-envelope-1--emails"} size={16} className="letter-icon" />A message from our CEO on SVB and
			upcoming resources. Read more here. →
		</a>
	)
}

// eslint-disable-next-line
function NavbarBanner({type, isAlwaysOpen}: {type: "sandbox" | "pilot" | "announcement"; isAlwaysOpen: boolean}) {
	const [isOpen, setIsOpen] = isAlwaysOpen ? [true, () => null] : useLocalStorage("showNavbarAnnouncementBanner", true)

	useEffect(() => {
		if (isOpen) {
			document.documentElement.style.setProperty("--navbar-height", "92px")
		}

		return () => {
			// on unmount
			document.documentElement.style.setProperty("--navbar-height", "60px")
		}
	}, [isOpen])

	if (!isOpen) {
		return null
	}

	let bannerComponent
	switch (type) {
		case "sandbox": {
			bannerComponent = <SandboxBanner isOpen={isOpen} />
			break
		}
		case "pilot": {
			bannerComponent = <PilotBanner isOpen={isOpen} />
			break
		}
		case "announcement": {
			bannerComponent = <AnnouncementBanner />
			break
		}
	}

	return (
		<div className={classNames("navbar-banner-wrapper", type, isOpen && "open")}>
			{bannerComponent}

			{!isAlwaysOpen ? (
				<span className="close-button" onClick={() => setIsOpen(false)}>
					<Icon icon={"interface-delete-interface-essential"} size={12} color="#808285" />
				</span>
			) : null}
		</div>
	)
}

export default function Navbar() {
	const isHomePage = useLocation()?.pathname === "/"
	// const isOrgUnitPilot = useIsOrgUnitPilot()
	// const showPilotBanner = window.location.pathname != "/pilot-overview" && isOrgUnitPilot
	// const isOrgUser = useIsOrgUser()
	const isOnbehalfUser = useIsOnBehalfUser()
	// const showSandboxBanner = isSandboxOrLocalEnv() && isOrgUser
	const userTokenData = useUserClaimsData()
	const orgName = userTokenData.org

	// const showAnnouncementBanner = true

	return (
		<header className="navbar">
			<div className={classNames("top-nav-container-wrapper")} style={{position: "relative"}}>
				<div className={classNames("top-nav-container", {"top-nav-container--unfixed": isHomePage})}>
					{/* {showPilotBanner && <NavbarBanner type="pilot" isAlwaysOpen={true} />} */}
					{/* {showSandboxBanner && <NavbarBanner type="sandbox" isAlwaysOpen={false} />} */}
					{/*{showAnnouncementBanner && <NavbarBanner type={"announcement"} isAlwaysOpen={false}/>}*/}

					<nav className={classNames("navbar", {"is-homepage": isHomePage})}>
						<NavLink to="/" className="navbar-logo-wrapper">
							<span className="logo-wrapper">
								<img src={LogoLight} alt={"Unit"} />
							</span>
							<NavbarEnvironmentIndicator isExpanded={true} />
						</NavLink>
						<div className="navbar-right-wrapper">
							{isOnbehalfUser && <NavbarOnBelalfBanner name={orgName} />}
							<NavbarTimezoneDropdown />
							<NavbarDeveloperDropdown />
							<NavbarUserDropdown />
						</div>
					</nav>
				</div>
			</div>
		</header>
	)
}
